import { useMemo, useState } from "react"

import useAppHotkey from "@hooks/useAppHotkey"
import useHasAccess from "@hooks/useHasAccess"
import useSlugExtractor from "@hooks/useSlugExtractor"
import useUser from "@hooks/useUser"

import { faIcon } from "@utils/faIcon"

import { Menu } from "@organisms"
import type { MenuItemType } from "@organisms/Menu/Menu.types"
import NavSidebarItem from "@organisms/NavSidebar/components/NavSidebarItem/NavSidebarItem"

import { BILLS_ROUTES } from "@routes/bills"
import { CLIENTS_ROUTES } from "@routes/clients"
import { CREDIT_NOTES_ROUTES } from "@routes/creditNotes"
import { ESTIMATES_ROUTES } from "@routes/estimates"
import { INVOICES_ROUTES } from "@routes/invoices"
import { JOBS_ROUTES } from "@routes/jobs"
import { PRICEBOOK_ITEMS_ROUTES } from "@routes/pricebook-items"
import { PURCHASE_ORDER_ROUTES } from "@routes/purchase-orders"
import { VENDOR_ROUTES } from "@routes/vendor"

import { APIDeckTaxStrategies, AccountingIntegrations } from "@constants/choices"
import { objectMap } from "@constants/objectMap"
import { PERMISSION_LEVEL } from "@constants/permissionLevel"

import styles from "./NavSidebarCreateButton.module.scss"
import { NavSidebarCreateButtonProps } from "./NavSidebarCreateButton.types"

const OPEN_OVERFLOW_MENU_HOTKEY = "c"

export default function NavSidebarCreateButton(props: NavSidebarCreateButtonProps) {
    const { _isFocused = false, _isHovered = false, isPressed = false, isDisabled = false } = props

    const { user } = useUser()

    const { hasEntitlement, hasPermission } = useHasAccess()

    const isUsingTaxRates =
        user?.service_company?.use_taxes &&
        !(
            user?.service_company?.accounting_integration === AccountingIntegrations.apideck &&
            user?.service_company?.apideck_tax_strategy !== APIDeckTaxStrategies.normal
        )

    const [isOpen, setIsOpen] = useState<boolean>(false)

    const toggleMenu = () => setIsOpen(!isOpen)

    useAppHotkey(
        OPEN_OVERFLOW_MENU_HOTKEY,
        () => {
            if (!isDisabled) {
                toggleMenu()
            }
        },
        {
            enabled: !isDisabled,
        },
        [isDisabled],
    )

    const [
        estimateCreateRoute,
        jobCreateRoute,
        invoiceCreateRoute,
        creditNoteCreateRoute,
        purchaseOrderCreateRoute,
        billCreateRoute,
        vendorCreateRoute,
        clientCreateRoute,
        pricebookItemServiceCreateRoute,
        pricebookItemPartOrMaterialCreateRoute,
        pricebookItemMiscellaneousCreateRoute,
        pricebookItemDiscountCreateRoute,
        pricebookItemTaxRatesCreateRoute,
    ] = useSlugExtractor([
        ESTIMATES_ROUTES.CREATE,
        JOBS_ROUTES.CREATE,
        INVOICES_ROUTES.CREATE,
        CREDIT_NOTES_ROUTES.CREATE,
        PURCHASE_ORDER_ROUTES.CREATE,
        BILLS_ROUTES.CREATE,
        VENDOR_ROUTES.CREATE,
        CLIENTS_ROUTES.CREATE,
        PRICEBOOK_ITEMS_ROUTES.SERVICE.CREATE,
        PRICEBOOK_ITEMS_ROUTES.PART_OR_MATERIAL.CREATE,
        PRICEBOOK_ITEMS_ROUTES.MISCELLANEOUS.CREATE,
        PRICEBOOK_ITEMS_ROUTES.DISCOUNT.CREATE,
        PRICEBOOK_ITEMS_ROUTES.TAX_RATES.CREATE,
    ])

    const menuContentItems = useMemo(() => {
        const items: MenuItemType[] = []

        if (
            hasEntitlement("entitlement_estimates_enabled") &&
            hasPermission("estimates_create_permission", PERMISSION_LEVEL.RESTRICTED)
        ) {
            items.push({
                icon: objectMap.estimate.icon,
                path: estimateCreateRoute,
                title: objectMap.estimate.label,
            })
        }

        if (
            hasEntitlement("entitlement_jobs_enabled") &&
            hasPermission("jobs_create_permission", PERMISSION_LEVEL.RESTRICTED)
        ) {
            items.push({
                icon: objectMap.job.icon,
                path: jobCreateRoute,
                title: objectMap.job.label,
            })
        }

        if (
            hasEntitlement("entitlement_invoices_enabled") &&
            hasPermission("invoices_create_permission", PERMISSION_LEVEL.RESTRICTED)
        ) {
            items.push({
                icon: objectMap.invoice.icon,
                path: invoiceCreateRoute,
                title: objectMap.invoice.label,
            })
        }

        if (
            user?.service_company?.feature_credit_notes_enabled &&
            hasEntitlement("entitlement_invoices_enabled") &&
            hasPermission("invoices_create_permission", PERMISSION_LEVEL.RESTRICTED)
        ) {
            items.push({
                icon: objectMap.credit_note.icon,
                path: creditNoteCreateRoute,
                title: objectMap.credit_note.label,
            })
        }

        if (
            hasEntitlement("entitlement_purchase_orders_enabled") &&
            hasPermission("purchase_orders_create_permission", PERMISSION_LEVEL.RESTRICTED)
        ) {
            items.push({
                icon: objectMap.purchase_order.icon,
                path: purchaseOrderCreateRoute,
                title: objectMap.purchase_order.label,
            })
        }

        if (
            hasEntitlement("entitlement_bills_enabled") &&
            hasPermission("bills_create_permission", PERMISSION_LEVEL.RESTRICTED)
        ) {
            items.push({
                icon: objectMap.bill.icon,
                path: billCreateRoute,
                title: objectMap.bill.label,
            })
        }

        if (hasPermission("vendors_create_permission", PERMISSION_LEVEL.FULL)) {
            items.push({
                icon: objectMap.vendor.icon,
                path: vendorCreateRoute,
                title: objectMap.vendor.label,
            })
        }

        if (hasPermission("clients_create_permission", PERMISSION_LEVEL.FULL)) {
            items.push({
                icon: objectMap.client.icon,
                path: clientCreateRoute,
                title: objectMap.client.label,
            })
        }

        if (hasPermission("pricebook_create_permission", PERMISSION_LEVEL.FULL)) {
            items.push({
                title: objectMap.pricebook_item.label,
                icon: objectMap.pricebook_item.icon,
                submenu: {
                    header: { title: "Create Pricebook Item", showCloseButton: true },
                    className: styles.pricebookSubMenu,
                    items: [
                        {
                            type: "group",
                            items: [
                                {
                                    title: "Service",
                                    path: pricebookItemServiceCreateRoute,
                                },
                                {
                                    title: "Part or Material",
                                    path: pricebookItemPartOrMaterialCreateRoute,
                                },
                                {
                                    title: "Other",
                                    path: pricebookItemMiscellaneousCreateRoute,
                                },
                                {
                                    title: "Discount",
                                    path: pricebookItemDiscountCreateRoute,
                                },
                                ...(isUsingTaxRates
                                    ? [
                                          {
                                              title: "Tax Rate",
                                              path: pricebookItemTaxRatesCreateRoute,
                                          } as const,
                                      ]
                                    : []),
                            ],
                        },
                    ],
                },
            })
        }

        return items
    }, [
        hasEntitlement,
        hasPermission,
        isUsingTaxRates,
        estimateCreateRoute,
        jobCreateRoute,
        invoiceCreateRoute,
        creditNoteCreateRoute,
        purchaseOrderCreateRoute,
        billCreateRoute,
        vendorCreateRoute,
        clientCreateRoute,
        pricebookItemServiceCreateRoute,
        pricebookItemPartOrMaterialCreateRoute,
        pricebookItemMiscellaneousCreateRoute,
        pricebookItemDiscountCreateRoute,
        pricebookItemTaxRatesCreateRoute,
    ])

    return (
        <Menu
            title="Create"
            className={styles.menu}
            isOpen={isOpen}
            setIsOpen={setIsOpen}
            trigger={
                <NavSidebarItem
                    label="Create"
                    icon={faIcon({ name: "plus", style: "regular" })}
                    iconActive={faIcon({ name: "plus", style: "solid" })}
                    isDisabled={isDisabled}
                    _isFocused={_isFocused}
                    _isHovered={_isHovered}
                    isPressed={isPressed || isOpen}
                    onClick={toggleMenu}
                    modifier="hotkey"
                    symbolName="C"
                />
            }
            items={[{ type: "group", items: menuContentItems }]}
        />
    )
}
